import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth, type BlogPost } from '../lib/adminApi';

/**
 * Markdown editor with live preview. The form is on the left, rendered
 * preview on the right. On save we POST/PATCH the raw markdown – the
 * backend re-renders + sanitises before serving it publicly, so the
 * preview here is purely cosmetic.
 */
export function AdminBlogEditor() {
  const { slug: paramSlug } = useParams();
  const navigate = useNavigate();
  const isNew = !paramSlug;

  const [form, setForm] = useState({
    slug: '',
    title: '',
    excerpt: '',
    body: '# Heading\n\nWrite your post in Markdown.',
    coverImageUrl: '',
    author: 'Dormant Accounts Filing',
    status: 'DRAFT' as 'DRAFT' | 'PUBLISHED',
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
    if (isNew) return;
    adminApi
      .getPost(paramSlug!)
      .then((p: BlogPost) =>
        setForm({
          slug: p.slug,
          title: p.title,
          excerpt: p.excerpt || '',
          body: p.body,
          coverImageUrl: p.coverImageUrl || '',
          author: p.author,
          status: p.status,
        }),
      )
      .catch((e) => setError(String(e.message)))
      .finally(() => setLoading(false));
  }, [paramSlug, isNew, navigate]);

  // Read a file from the upload <input> as base64 and POST to the admin
  // upload endpoint. Backend streams it into Supabase Storage and returns
  // the public URL, which we drop into the coverImageUrl field.
  async function handleCoverUpload(file: File) {
    setUploadError(null);
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image is too large (max 5 MB).');
      return;
    }
    setUploading(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const r = String(reader.result || '');
          // Strip the leading "data:image/...;base64," — backend wants raw base64.
          resolve(r.replace(/^data:[^;]+;base64,/, ''));
        };
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      const { publicUrl } = await adminApi.uploadImage({
        filename: file.name,
        contentType: file.type,
        dataBase64,
      });
      setForm((f) => ({ ...f, coverImageUrl: publicUrl }));
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  // Live preview HTML – rendered + sanitised in the browser. Server does
  // the same on save before serving publicly; this is for editing feel.
  const previewHtml = useMemo(() => {
    try {
      const raw = marked.parse(form.body, { async: false }) as string;
      return DOMPurify.sanitize(raw);
    } catch {
      return '<p style="color:#991b1b">Could not render preview.</p>';
    }
  }, [form.body]);

  // Auto-generate slug from title for new posts.
  function setTitle(v: string) {
    setForm((f) => ({
      ...f,
      title: v,
      slug: isNew && !f.slug ? slugify(v) : f.slug,
    }));
  }

  async function save(publish: boolean) {
    setError(null);
    setSavedMsg(null);
    if (!form.title.trim() || !form.slug.trim() || !form.body.trim()) {
      setError('Title, slug and body are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        excerpt: form.excerpt.trim() || null,
        body: form.body,
        coverImageUrl: form.coverImageUrl.trim() || null,
        author: form.author.trim() || 'Dormant Accounts Filing',
        status: publish ? ('PUBLISHED' as const) : form.status,
      };
      if (isNew) {
        const saved = await adminApi.createPost({
          ...payload,
          excerpt: payload.excerpt || undefined,
          coverImageUrl: payload.coverImageUrl || undefined,
        });
        navigate(`/admin/blog/${saved.slug}/edit`);
      } else {
        const saved = await adminApi.updatePost(paramSlug!, payload);
        setForm((f) => ({ ...f, status: saved.status }));
        setSavedMsg(publish ? 'Saved & published.' : 'Saved.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-sm text-slate-500">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Link to="/admin/blog" className="text-sm text-brand-700 hover:underline">
        ← All posts
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {isNew ? 'New post' : 'Edit post'}
          </h1>
          {form.status === 'PUBLISHED' && !isNew && (
            <a
              href={`/blog/${form.slug}`}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-xs text-brand-700 hover:underline"
            >
              View live →
            </a>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => save(false)} disabled={saving} className="btn-ghost">
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button onClick={() => save(true)} disabled={saving} className="btn-primary">
            {form.status === 'PUBLISHED' ? 'Save changes' : 'Publish'}
          </button>
        </div>
      </div>

      {error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {savedMsg && (
        <div className="mt-4 rounded-md bg-green-50 p-3 text-sm text-green-700">{savedMsg}</div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        {/* ─── Editor side ─────────────────────────────────────────── */}
        <div className="space-y-4 rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <Field label="Title">
            <input
              value={form.title}
              onChange={(e) => setTitle(e.target.value)}
              className="input"
              placeholder="How to file your dormant company accounts"
            />
          </Field>
          <Field label="URL slug" hint={`/blog/${form.slug || '<slug>'}`}>
            <input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              className="input font-mono text-sm"
              placeholder="how-to-file-dormant-accounts"
            />
          </Field>
          <Field label="Excerpt (optional, appears on the list page)">
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
              className="input"
              rows={2}
              placeholder="A short summary that appears on the blog index."
            />
          </Field>
          <Field label="Cover image (optional)">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <label className="btn-ghost cursor-pointer">
                  {uploading ? 'Uploading…' : 'Upload from your computer'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCoverUpload(file);
                      e.target.value = ''; // allow re-uploading the same file
                    }}
                  />
                </label>
                {form.coverImageUrl && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, coverImageUrl: '' }))}
                    className="text-xs text-slate-500 hover:text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                value={form.coverImageUrl}
                onChange={(e) => setForm((f) => ({ ...f, coverImageUrl: e.target.value }))}
                className="input"
                placeholder="…or paste an image URL"
                type="url"
              />
              {uploadError && (
                <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{uploadError}</div>
              )}
              {form.coverImageUrl && (
                <img
                  src={form.coverImageUrl}
                  alt="Cover preview"
                  className="mt-1 max-h-40 rounded-md ring-1 ring-slate-200"
                  onError={() => setUploadError('Could not load this image URL.')}
                />
              )}
            </div>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Author">
              <input
                value={form.author}
                onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                className="input"
              />
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as 'DRAFT' | 'PUBLISHED' }))}
                className="input"
              >
                <option value="DRAFT">Draft</option>
                <option value="PUBLISHED">Published</option>
              </select>
            </Field>
          </div>
          <Field label="Body (Markdown)">
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              className="input font-mono text-sm"
              rows={20}
              placeholder="# Your heading&#10;&#10;Write the post in Markdown.&#10;&#10;- bullet 1&#10;- bullet 2&#10;&#10;[Link](https://example.com)"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Markdown supported: # heading, **bold**, *italic*, [link](url), - lists,
              `inline code`, &gt; blockquote, etc.
            </p>
          </Field>
        </div>

        {/* ─── Live preview side ─────────────────────────────────── */}
        <div className="rounded-xl bg-white p-5 ring-1 ring-slate-200">
          <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Live preview
            </div>
            <div className="text-[11px] text-slate-400">Updates as you type</div>
          </div>
          {form.title && (
            <h1 className="mb-4 text-2xl font-semibold text-slate-900">{form.title}</h1>
          )}
          {form.excerpt && (
            <p className="mb-4 text-base leading-relaxed text-slate-600">{form.excerpt}</p>
          )}
          <div
            className="blog-content text-slate-800"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>
    </AdminLayout>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="block text-sm font-medium text-slate-700">{label}</span>
        {hint && <span className="text-[11px] font-mono text-slate-400">{hint}</span>}
      </div>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}
